import { ApplicationRef, ChangeDetectionStrategy, Component, inject, NgZone, OnDestroy, OnInit } from '@angular/core';
import { NavigationError, Router } from '@angular/router';
import { EnvConfig } from '@core/config';
import { RouterService } from '@core/router/router.service';
import { akitaDevtools, persistState } from '@datorama/akita';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { ThemeSwitcherService } from '@feature/theme-manager/theme-switcher/theme-switcher.service';
import { Partner } from '@core/models/partner.model';
import { LogoService } from '@core/logo/logo.service';
import { MarketplaceLanguageListenerService } from '@core/marketplace/marketplace-language-listener.service';
import { AuthenticationService } from '@core/authentication/authentication.service';
import { IdleLogoutService } from '@core/authentication/idle-logout.service';
import { AppTranslateLoader } from '@core/app-translate-loader';
import { TranslateService } from '@ngx-translate/core';
import { distinctUntilChanged, filter, take } from 'rxjs/operators';
import { TypedFormGroup } from '@feature/forms/form-model';
import { BootstrapLoaderService } from '@core/loader/bootstrap-loader.service';
import { Automapper } from '@core/api/mapper';
import { isChunkLoadError } from './shared/chunk-retry';

@UntilDestroy()
@Component({
  selector: 'wai-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class AppComponent implements OnInit, OnDestroy {
  public asTypedFormGroup = (x: unknown): TypedFormGroup<any> => x as TypedFormGroup<any>;

  private env = inject(EnvConfig);
  private themeSwitcher = inject(ThemeSwitcherService);
  private ngZone = inject(NgZone);
  private routerService = inject(RouterService);
  private logoService = inject(LogoService);
  private router = inject(Router);
  private marketplaceLanguageListenerService = inject(MarketplaceLanguageListenerService);
  private authenticationService = inject(AuthenticationService);
  private idleLogoutService = inject(IdleLogoutService);
  private translateService = inject(TranslateService);
  private bootstrapLoader = inject(BootstrapLoaderService);
  private appRef = inject(ApplicationRef);

  private contentShown = false;
  private translationReady = false;
  private bootstrapReady = false;
  private appStable = false;
  private minDurationReached = false;
  private bootstrapStateInitialized = false;
  private minDurationTimer: ReturnType<typeof setTimeout>;
  private failsafeTimer: ReturnType<typeof setTimeout>;
  private readonly minLoaderDurationMs = 1500;
  private readonly loaderFailsafeDurationMs = 4000;
  private readonly overlayMessageLoading = 'Loading, please wait…';
  private readonly overlayMessageLanguageChange = 'Applying language change, please wait…';
  private readonly overlayMessageDelayText = 'Still getting things ready…';
  private overlayMessageDefault = this.overlayMessageLoading;

  constructor() {
    if (!this.env.production) {
      akitaDevtools(this.ngZone);
    }

    // Initialize loading overlay to prevent flash of untranslated content
    // Wait for backend translations before showing the app
    const initialLanguage = this.translateService.currentLang || this.translateService.defaultLang;

    const languageChangeInProgress = sessionStorage.getItem('languageChangeInProgress') === 'true';
    if (languageChangeInProgress) {
      this.overlayMessageDefault = this.overlayMessageLanguageChange;
    }
    this.setOverlayMessage(this.overlayMessageDefault);

    AppTranslateLoader.onLanguageLoaded(initialLanguage, () => {
      this.translationReady = true;
      this.tryHideContent();
    });

    this.minDurationTimer = setTimeout(() => {
      this.minDurationReached = true;
      this.tryHideContent();
    }, this.minLoaderDurationMs);

    this.failsafeTimer = setTimeout(() => {
      if (!this.contentShown) {
        this.setOverlayMessage(this.overlayMessageDelayText);
        this.translationReady = true;
        this.bootstrapReady = true;
        this.minDurationReached = true;
        this.showContent();
      }
    }, this.loaderFailsafeDurationMs);

    this.bootstrapLoader.loading$
      .pipe(distinctUntilChanged(), untilDestroyed(this))
      .subscribe((isLoading) => {
        this.bootstrapStateInitialized = true;
        this.bootstrapReady = !isLoading;
        if (!isLoading) {
          this.tryHideContent();
        }
      });

    this.appRef.isStable
      .pipe(
        filter((isStable) => isStable === true),
        take(1),
        untilDestroyed(this),
      )
      .subscribe(() => {
        this.appStable = true;
        this.tryHideContent();
      });

    this.initPersistentState();
    this.routerService.init();

    const shouldReloadOnChunkError = (err: unknown): boolean => isChunkLoadError(err);

    const avoidReloadLoop = (): boolean => {
      const key: string = '__lastChunkReloadTs__';
      const now: number = Date.now();
      const last: number = Number(sessionStorage.getItem(key) || 0);
      if (now - last < 5000) {
        return false;
      }
      sessionStorage.setItem(key, String(now));
      return true;
    };

    this.router.events.subscribe((e) => {
      if (e instanceof NavigationError && shouldReloadOnChunkError(e.error) && avoidReloadLoop()) {
        this.reloadPage();
      }
    });

    // Force instantiation of MarketplaceLanguageListenerService
    // This ensures the service is created and starts listening to language changes
    // The service will automatically handle language changes and reload page data
    if (this.marketplaceLanguageListenerService) {
      // Service is properly instantiated and will handle language changes
    }

    window.addEventListener('unhandledrejection', (ev: PromiseRejectionEvent) => {
      if (shouldReloadOnChunkError((ev as any).reason) && avoidReloadLoop()) {
        this.reloadPage();
      }
    });
  }

  public reloadPage(): void {
    location.reload();
  }

  ngOnInit(): void {
    // Start idle logout watcher (logs out after 1 hour inactivity while authenticated)
    this.idleLogoutService.start();

    this.authenticationService
      .isAuthenticated()
      .pipe(take(1))
      .subscribe((isAuthenticated) => {
        if (isAuthenticated) {
          this.themeSwitcher.fetchTheme();
        } else if (!localStorage.getItem('initialThemeParams')) {
          this.themeSwitcher.fetchTheme();
        } else {
          const rawData = JSON.parse(localStorage.getItem('activeTheme'));
          const partner = Automapper.create(Partner).toModel(rawData) as Partner;
          this.themeSwitcher.savePrimaryAndAccentColor(partner.primaryColor, partner.secondaryColor);
          this.themeSwitcher.saveSidebarColors(partner.sidebarBackgroundColor, partner.sidebarTextColor);
          this.themeSwitcher.changeToPartnerTitleAndFevicon(partner);
          this.themeSwitcher.replaceKapnativeBasedOnCondition(partner);
          const lightUrl = partner.logoImageLight?.url ?? (partner as any).logo_image_light?.url;
          const darkUrl = partner.logoImageDark?.url ?? (partner as any).logo_image_dark?.url ?? lightUrl;
          this.logoService.setLogo(lightUrl, 'light');
          this.logoService.setLogo(darkUrl, 'dark');
        }
      });
  }

  public ngOnDestroy(): void {
    if (this.minDurationTimer) {
      clearTimeout(this.minDurationTimer);
      this.minDurationTimer = undefined;
    }

    if (this.failsafeTimer) {
      clearTimeout(this.failsafeTimer);
      this.failsafeTimer = undefined;
    }

    this.routerService.destroy();
  }

  private initPersistentState(): void {
    persistState({ include: ['AppStore', 'organizations'] });
  }

  /**
   * Remove loading overlay and show app content.
   * Called when backend translations have loaded or after timeout.
   */
  private showContent(): void {
    if (this.contentShown) {
      return;
    }

    this.contentShown = true;
    this.translationReady = true;
    this.bootstrapReady = true;
    this.minDurationReached = true;

    if (this.minDurationTimer) {
      clearTimeout(this.minDurationTimer);
      this.minDurationTimer = undefined;
    }

    if (this.failsafeTimer) {
      clearTimeout(this.failsafeTimer);
      this.failsafeTimer = undefined;
    }

    const overlay = document.getElementById('translation-loading-overlay');

    if (overlay) {
      this.setOverlayMessage(this.overlayMessageDefault);
      // Fade out overlay
      overlay.style.transition = 'opacity 0.2s ease-out';
      overlay.style.opacity = '0';

      // Remove overlay and class after fade completes
      setTimeout(() => {
        overlay.style.display = 'none';
        document.body.classList.remove('translations-loading');
      }, 200);
    } else {
      // Fallback if overlay doesn't exist
      document.body.classList.remove('translations-loading');
    }

    sessionStorage.removeItem('languageChangeInProgress');
  }

  private tryHideContent(): void {
    if (this.contentShown) {
      return;
    }

    if (!this.bootstrapStateInitialized || !this.translationReady || !this.bootstrapReady || !this.appStable || !this.minDurationReached) {
      return;
    }

    this.showContent();
  }

  private setOverlayMessage(message: string): void {
    const messageElement = document.getElementById('translation-loading-message');
    if (messageElement) {
      messageElement.textContent = message;
    }
  }
}
